import type { JsonSchemaObject, GenerateContext } from "../types.js";
import { generateFromRegex } from "../pattern/regex-gen.js";
import { pad2 } from "../utils/helpers.js";

const DEFAULT_CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function canPatternMatchNonEmpty(pattern: string): boolean {
  // Patterns that only match empty string
  if (pattern === "^" || pattern === "$" || pattern === "^$") {
    return false;
  }
  return true;
}

export function generateString(
  schema: JsonSchemaObject,
  ctx: GenerateContext
): string {
  // Check format first
  if (schema.format) {
    // Handle date-time format with min/max constraints
    if (schema.format === "date-time" && (ctx.minDateTime !== undefined || ctx.maxDateTime !== undefined)) {
      return generateDateTimeWithRange(ctx);
    }

    const formatGen = ctx.formatRegistry.get(schema.format);
    if (formatGen) {
      const result = formatGen(ctx.random);
      // Respect length constraints from both schema and context options
      const effectiveMinLength = ctx.minLength ?? schema.minLength;
      const effectiveMaxLength = ctx.maxLength ?? schema.maxLength;
      if (effectiveMinLength !== undefined && result.length < effectiveMinLength) {
        return padString(result, effectiveMinLength, ctx);
      }
      if (effectiveMaxLength !== undefined && result.length > effectiveMaxLength) {
        return result.slice(0, effectiveMaxLength);
      }
      return result;
    }
  }

  // Check pattern
  if (schema.pattern) {
    const effectiveMinLength = ctx.minLength ?? schema.minLength;
    const effectiveMaxLength = ctx.maxLength ?? schema.maxLength;
    const maxAttempts = 50;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = generateFromRegex(schema.pattern, ctx.random);
      if (effectiveMinLength !== undefined && result.length < effectiveMinLength) {
        continue;
      }
      if (effectiveMaxLength !== undefined && result.length > effectiveMaxLength) {
        continue;
      }
      return result;
    }
    // Fallback: after max attempts, try to generate valid output
    // If pattern only matches empty string (^, $, ^$), return empty string as last resort
    // Otherwise, pad to meet minLength
    const result = generateFromRegex(schema.pattern, ctx.random);
    
    // Check if pattern can match non-empty strings
    const patternCanMatchNonEmpty = canPatternMatchNonEmpty(schema.pattern);
    
    if (effectiveMinLength !== undefined && result.length < effectiveMinLength) {
      // Only pad if pattern can match non-empty strings
      if (patternCanMatchNonEmpty) {
        return padString(result, effectiveMinLength, ctx);
      }
      // Pattern only matches empty string, return as-is (may violate minLength)
    }
    if (effectiveMaxLength !== undefined && result.length > effectiveMaxLength) {
      return result.slice(0, effectiveMaxLength);
    }
    return result;
  }

  const minLen = schema.minLength ?? 0;
  const maxLen = schema.maxLength ?? Math.max(minLen + 10, 10);
  const length = ctx.random.int(minLen, maxLen);

  let result = "";
  for (let i = 0; i < length; i++) {
    result += ctx.random.pick([...DEFAULT_CHARS]);
  }

  // Apply context maxLength/minLength as final truncation/padding (not for generation)
  if (ctx.maxLength !== undefined && result.length > ctx.maxLength) {
    result = result.slice(0, ctx.maxLength);
  }
  if (ctx.minLength !== undefined && result.length < ctx.minLength) {
    result = padString(result, ctx.minLength, ctx);
  }

  return result;
}

function padString(str: string, targetLength: number, ctx: GenerateContext): string {
  while (str.length < targetLength) {
    str += ctx.random.pick([...DEFAULT_CHARS]);
  }
  return str;
}

function generateDateTimeWithRange(ctx: GenerateContext): string {
  const minDt = ctx.minDateTime ? new Date(ctx.minDateTime) : new Date("1970-01-01");
  const maxDt = ctx.maxDateTime ? new Date(ctx.maxDateTime) : new Date();

  const minTime = minDt.getTime();
  const maxTime = maxDt.getTime();
  const randomTime = minTime + ctx.random.next() * (maxTime - minTime);
  const date = new Date(randomTime);

  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  return `${year}-${month}-${day}T01:01:01.0Z`;
}
